import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Compass, MapPin, Calendar, Users, Check, X, Share2, Send, ArrowRightLeft, Clock,
  Palette, Upload, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { getJourney } from "../lib/adventureApi";
import PodcastPlayer from "../components/ra/PodcastPlayer";
import ReserveDialog from "../components/ra/ReserveDialog";
import { computeJourney, buildTimeline } from "../lib/pricing";
import { numberedIcon } from "../lib/markers";
import { MONTHS, typeMeta, SERVICE_LEVELS } from "../lib/adventureConstants";
import { getUser, isTradeUser } from "../lib/auth";
import { useCommissionRate } from "../lib/commission";
import { useMarket } from "../lib/market";
import CountryBoundary from "../components/adventure/CountryBoundary";

const RA_BRAND = { name: "Responsible Adventures", logo: null, contact: "raj@responsibleadventures.com · +977 9801082108" };
const AU_BRAND = {
  name: "Responsible Adventures Australia",
  logo: null,
  contact: "Glenn Rodricks · 0414 402 289 · glenn@cosell.com.au",
  legal: "The Great Aus Dream Pty Ltd t/a Responsible Adventures AU · In partnership with Responsible Adventures",
};

function BrandingDialog({ open, onClose, agency, onSave }) {
  const [f, setF] = useState(agency);
  useEffect(() => { setF(agency); }, [agency, open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Logo must be under 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-md z-[1200]" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">White-label this proposal</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink-secondary -mt-2 mb-1">Add your agency details — they'll appear on the proposal you share with your client instead of Wayfinder.</p>
        <div className="space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Agency name</label>
            <input data-testid="agency-name-input" value={f.name || ""} onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Summit & Silk Travel"
              className="w-full mt-1 px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Contact line (optional)</label>
            <input data-testid="agency-contact-input" value={f.contact || ""} onChange={(e) => set("contact", e.target.value)}
              placeholder="hello@youragency.com · +1 555 0100"
              className="w-full mt-1 px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-moss">Agency logo (PNG/JPG, max 1MB)</label>
            <div className="flex items-center gap-3 mt-1">
              {f.logo ? (
                <div className="relative">
                  <img src={f.logo} alt="logo" className="h-12 w-auto max-w-[140px] object-contain bg-white border border-sand-dark rounded-sm p-1" />
                  <button data-testid="agency-logo-remove" onClick={() => set("logo", "")}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-ink text-white rounded-full flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-3 py-2 rounded-sm border border-dashed border-sand-dark text-sm text-ink-secondary cursor-pointer hover:border-moss transition-colors">
                  <Upload className="w-4 h-4" /> Upload logo
                  <input data-testid="agency-logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} />
                </label>
              )}
            </div>
          </div>
          <button data-testid="agency-save" onClick={() => { onSave(f); onClose(); toast.success("Proposal branding saved"); }}
            className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold tracking-wide transition-colors">
            Save branding
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Review() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const user = getUser();
  const { market, price, priceNote } = useMarket();
  const agency = user && user.role === "agency"
    ? { name: user.company_name, logo: user.logo_url, contact: [user.company_email, user.phone].filter(Boolean).join(" · ") }
    : null;
  const brand = agency || (market === "au" ? AU_BRAND : RA_BRAND);

  useEffect(() => { getJourney(id).then(setData).catch(() => {}); }, [id]);
  const commissionRate = useCommissionRate(data?.journey?.source_tour_slug);

  const modulesById = useMemo(() => Object.fromEntries((data?.modules || []).map((m) => [m.id, m])), [data]);
  const locationsById = useMemo(() => Object.fromEntries((data?.locations || []).map((l) => [l.id, l])), [data]);

  if (!data) {
    return <div className="h-screen flex items-center justify-center bg-base text-ink-secondary">
      <Compass className="w-6 h-6 animate-spin text-terracotta mr-2" /> Preparing your itinerary…
    </div>;
  }

  const { journey, region } = data;
  const items = journey.items.map((it, i) => ({ ...it, uid: `r-${i}` }));
  const pricing = computeJourney(items, modulesById, journey.service_level, journey.trip.travellers || 1);
  const timeline = buildTimeline(items, modulesById, locationsById);
  const serviceName = SERVICE_LEVELS.find((s) => s.id === journey.service_level)?.name;

  const stops = items.filter((it) => it.kind !== "transport").map((it, idx) => {
    const m = modulesById[it.module_id]; const loc = locationsById[m?.location_id];
    return loc ? { n: idx + 1, lat: loc.lat, lng: loc.lng } : null;
  }).filter(Boolean);
  const line = stops.map((s) => [s.lat, s.lng]);

  const allInclusions = [...new Set(items.filter((i) => i.kind !== "transport")
    .flatMap((i) => modulesById[i.module_id]?.inclusions || []))];
  const allExclusions = [...new Set(items.filter((i) => i.kind !== "transport")
    .flatMap((i) => modulesById[i.module_id]?.exclusions || []))];

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Share link copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-base" data-testid="review-page">
      {/* Cover */}
      <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={region.image} alt={region.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-5 sm:p-8">
          <button data-testid="review-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back to builder
          </button>
          <div className="flex items-center gap-2">
            {!agency && (
              <button data-testid="add-logo-btn" onClick={() => navigate("/trade")} className="flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/30 text-white px-3.5 py-2 rounded-sm text-sm font-medium hover:bg-white/25 transition-colors">
                <Palette className="w-4 h-4" /> Add your logo
              </button>
            )}
            <button data-testid="share-btn" onClick={share} className="flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/30 text-white px-3.5 py-2 rounded-sm text-sm font-medium hover:bg-white/25 transition-colors">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-5xl mx-auto w-full">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4" data-testid="proposal-brand">
            {brand.logo && <img src={brand.logo} alt="brand" className="h-9 w-auto max-w-[130px] object-contain bg-white/90 rounded-sm p-1" />}
            <span className="font-display font-bold text-white text-lg tracking-tight">{brand.name}</span>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-mono text-xs tracking-[0.25em] uppercase text-white/70 mb-3">
            Client proposal · {serviceName} level
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-extrabold text-4xl sm:text-6xl text-white tracking-tight leading-none">
            {region.name} Expedition
          </motion.h1>
          <div className="flex flex-wrap gap-4 mt-5 text-white/90 text-sm font-mono">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{pricing.duration} DAYS</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{journey.trip.travellers} TRAVELLERS</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{stops.length} STOPS</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{MONTHS[(journey.trip.month || 1) - 1]}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-10 py-10 grid lg:grid-cols-3 gap-10">
        {/* Left: itinerary */}
        <div className="lg:col-span-2 space-y-10">
          {/* Route map */}
          <section>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Your route</h2>
            <div className="h-72 rounded-md overflow-hidden border border-sand-dark">
              <MapContainer center={region.center} zoom={region.zoom} style={{ height: "100%", width: "100%" }} zoomControl={false} attributionControl={false} scrollWheelZoom={false}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" maxZoom={18} />
                <CountryBoundary regionId={region.id} />
                {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#D95A40", weight: 3, dashArray: "1 8", lineCap: "round" }} />}
                {stops.map((s) => <Marker key={s.n} position={[s.lat, s.lng]} icon={numberedIcon(s.n)} />)}
              </MapContainer>
            </div>
          </section>

          {/* Day by day */}
          <section>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-5">Day by day</h2>
            <div className="space-y-0">
              {timeline.map((row, i) => {
                const isT = row.type === "transport";
                const meta = typeMeta(row.type);
                return (
                  <div key={row.key} className="flex gap-4" data-testid={`review-day-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center font-mono text-sm font-bold text-white shrink-0"
                        style={{ background: isT ? "#3A6FB0" : meta.color }}>
                        {isT ? <ArrowRightLeft className="w-4 h-4" /> : row.dayRange}
                      </div>
                      {i < timeline.length - 1 && <div className="w-px flex-1 bg-sand-dark my-1" />}
                    </div>
                    <div className="flex-1 pb-8">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                        {isT ? "Transfer" : `Day ${row.dayRange} · ${row.location}`}
                      </p>
                      <h3 className="font-display font-bold text-lg tracking-tight mt-0.5">{row.title}</h3>
                      {row.module && (
                        <div className="flex gap-4 mt-3">
                          <img src={row.module.image} alt="" className="w-28 h-20 object-cover rounded-sm shrink-0" />
                          <div>
                            <p className="text-sm text-ink-secondary leading-relaxed line-clamp-3">{row.module.description}</p>
                            <p className="font-mono text-[11px] text-ink-muted mt-1.5 flex items-center gap-2">
                              <Clock className="w-3 h-3" />{row.module.days}d · {row.module.difficulty} · max {row.module.elevation_max}m
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Inclusions */}
          <section className="grid sm:grid-cols-2 gap-8">
            <div>
              <h2 className="font-display font-bold text-lg tracking-tight mb-3">What's included</h2>
              <ul className="space-y-1.5">
                {allInclusions.map((inc) => (
                  <li key={inc} className="flex items-start gap-2 text-sm text-ink-secondary"><Check className="w-4 h-4 text-moss shrink-0 mt-0.5" />{inc}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-display font-bold text-lg tracking-tight mb-3">Not included</h2>
              <ul className="space-y-1.5">
                {allExclusions.map((ex) => (
                  <li key={ex} className="flex items-start gap-2 text-sm text-ink-muted"><X className="w-4 h-4 shrink-0 mt-0.5" />{ex}</li>
                ))}
              </ul>
            </div>
          </section>
        </div>

        {/* Right: price + booking */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 border border-sand-dark rounded-md bg-white p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-moss">Investment</p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="font-display font-extrabold text-4xl tracking-tight">{price(pricing.perPerson)}</span>
              <span className="text-sm text-ink-muted">/ person</span>
            </div>
            <div className="mt-4 space-y-2 py-4 border-y border-sand-dark">
              {Object.entries(pricing.breakdown).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm text-ink-secondary"><span>{k}</span><span className="font-mono">${v}</span></div>
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <span className="text-sm font-semibold">Total ({journey.trip.travellers} pax)</span>
              <span className="font-mono font-extrabold text-xl text-terracotta">{price(pricing.total)}</span>
            </div>
            <div className="flex justify-between mt-1 text-xs text-ink-muted">
              <span>Deposit to confirm (20%)</span><span className="font-mono">{price(pricing.deposit)}</span>
            </div>
            {priceNote && (
              <p className="text-[11px] text-ink-muted mt-2" data-testid="review-price-note">{priceNote}</p>
            )}

            {isTradeUser() && (
              <div className="flex justify-between mt-3 px-3 py-2 rounded-sm bg-moss-light" data-testid="review-commission">
                <span className="text-sm font-semibold text-moss">Your commission ({Math.round(commissionRate * 100)}%)</span>
                <span className="font-mono text-sm font-extrabold text-moss">{price(Math.round(pricing.total * commissionRate))}</span>
              </div>
            )}

            <ReserveDialog tripName={`${region.name} Expedition`} journeyId={journey.id} tourSlug={journey.source_tour_slug}
              triggerClassName="w-full mt-6 flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors" />

            {isTradeUser() && (
              <button data-testid="enquire-btn" onClick={() => toast.success("Proposal sent", { description: "Your client will receive this branded itinerary. Our team is on standby to confirm." })}
                className="w-full mt-2 flex items-center justify-center gap-2 border border-sand-dark hover:border-moss h-11 rounded-sm font-medium text-sm transition-colors">
                <Send className="w-4 h-4 text-moss" /> Send proposal to client
              </button>
            )}
            <button data-testid="edit-journey-btn" onClick={() => navigate(`/build/${journey.region}${journey.source_tour_slug ? `?tour=${journey.source_tour_slug}` : ""}`)}
              className="w-full mt-2 h-11 rounded-sm border border-sand-dark font-medium text-sm hover:border-moss transition-colors">
              Keep editing
            </button>

            {journey.source_tour_slug && (
              <div className="mt-4 pt-4 border-t border-sand-dark">
                <PodcastPlayer slug={journey.source_tour_slug} tourName={`${region.name} Expedition`}
                  triggerClassName="w-full flex items-center justify-center gap-2 bg-ink hover:bg-ink/90 text-white h-11 rounded-sm font-semibold text-sm tracking-wide transition-colors" />
                <p className="text-[11px] text-ink-muted mt-2 text-center">Hear founder Raj &amp; traveller Simone on what to expect</p>
              </div>
            )}

            <p className="text-[11px] text-ink-muted mt-4 text-center">
              Quote valid for 30 days · Prices per person, twin share
              <br />Prepared by {brand.name}{brand.contact ? ` · ${brand.contact}` : ""}
              {brand.legal && <><br />{brand.legal}</>}
            </p>
          </div>
        </div>
      </div>

      {/* branding derives from the signed-in agency account */}
    </div>
  );
}
